import type WebApiClient from "../../api/WebApiClient.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { type IFileUpload } from "./file-upload.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import {
  type Database,
  type InputSequenceUploadInput,
  type InputSequenceUploadValidation,
  type SequenceType,
  type ValidationError,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import {
  DELETE_SEQUENCES_FAILURE,
  DELETE_SEQUENCES_REQUEST,
  DELETE_SEQUENCES_SUCCESS,
  FILE_GLOBAL_VALIDATION_FAILED,
  FILE_VALIDATION_COMPLETE,
  FILE_VALIDATION_FAILED,
  FILE_VALIDATION_REQUEST,
  FILE_VALIDATION_WAITING,
  SUBMIT_BLAST_JOB_FAILURE,
} from "@/redux/action-types.ts";

/**
 * State definition
 */
export interface ISequencesState {}

const initialState: ISequencesState = {};

/**
 * Reducers
 */
export default createReducer(initialState, {});

/**
 * Action creators
 */

export function validateSequenceFilesForUpload(sequenceType: SequenceType, filesToBeValidated: IFileUpload[]) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<ValidationError[]> => {
    if (filesToBeValidated) {
      dispatch({ type: FILE_VALIDATION_REQUEST, payload: filesToBeValidated });
    }

    const sequenceUploadValidationInput: Partial<InputSequenceUploadValidation> = {
      // TODO: inconsistent type handling here:  sqt.id and dat.id are numbers, in gql its a string, then it is parsed to an integer again
      sequenceType: sequenceType?.id?.toString(),
      files: filesToBeValidated?.map((file: IFileUpload) => {
        return { fileName: file.file?.input, filePath: file.file?.uploaded };
      }),
    };

    return webApiClient
      .validateSequenceUpload(sequenceUploadValidationInput)
      .then((validationErrors: ValidationError[]) => {
        if (filesToBeValidated) {
          const hasErrors = validationErrors?.length;
          if (hasErrors) {
            // some validationError.inputName can be null: that means we consider all files as failed
            // (eg due to some form-wide validation like the accumulated number of seqs is too large)
            const globalValidationErrors: ValidationError[] = validationErrors.filter(
              (error: ValidationError) => !error.inputName,
            );

            const fileValidationErrors: ValidationError[] = validationErrors.filter(
              (error: ValidationError) => error.inputName,
            );

            // dispatch a flag for files whose validation succeeded or failed
            dispatch({
              type: FILE_VALIDATION_WAITING,
              payload: filesToBeValidated.map((fileUpload) => fileUpload.file),
            });

            if (globalValidationErrors.length > 0) {
              dispatch({ type: FILE_GLOBAL_VALIDATION_FAILED });
            }

            if (fileValidationErrors.length > 0) {
              dispatch({ type: FILE_VALIDATION_FAILED, payload: fileValidationErrors });
            }
          } else {
            dispatch({ type: FILE_VALIDATION_COMPLETE, payload: filesToBeValidated });
          }
        }
        return validationErrors;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: FILE_GLOBAL_VALIDATION_FAILED,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function submitSequenceUpload(sequenceUploadInput: InputSequenceUploadInput) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient
      .submitSequencesUpload(sequenceUploadInput)
      .then((result: boolean) => {
        notify.info({
          title: selMessage({ id: "upload.database.sequence.successTitle" }),
          children: SelMessage({
            id: "upload.database.sequence.successMessage",
          }),
        });

        return result;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SUBMIT_BLAST_JOB_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function deleteUploadedSequences(sequenceIds: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Database> => {
    dispatch({ type: DELETE_SEQUENCES_REQUEST });

    return webApiClient
      .deleteUploadedSequences(sequenceIds)
      .then((success: boolean) => {
        dispatch({
          type: DELETE_SEQUENCES_SUCCESS,
          payload: success,
        });

        notify.info({
          title: selMessage({
            id: "upload.database.sequence.deletion.successTitle",
            values: { sequenceCount: sequenceIds.length },
          }),
          children: SelMessage({
            id: "upload.database.sequence.deletion.successMessage",
            values: { sequenceCount: sequenceIds.length },
          }),
        });

        return success;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_SEQUENCES_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

/***  Utils  ***/
